/**
 * C5 — Axios 401-Interceptor mit automatischem Token-Refresh
 *
 * Konfiguriert die globale axios-Instanz mit:
 * - Request-Interceptor: fügt automatisch Authorization-Header hinzu
 * - Response-Interceptor: bei 401 wird der Token per Refresh-Endpoint erneuert
 *   und der ursprüngliche Request wird wiederholt. Läuft parallel das gleiche,
 *   warten alle auf einen einzigen Refresh-Request (Queue-Mechanismus).
 */
import axios from 'axios';
import { API_ENDPOINTS } from './config/api';

let isRefreshing = false;
let failedQueue = [];

function processQueue(error, token = null) {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
}

function getStoredUser() {
  try {
    return JSON.parse(localStorage.getItem('user'));
  } catch {
    return null;
  }
}

function redirectToLogin() {
  localStorage.removeItem('user');
  window.location.href = '/login';
}

// Request-Interceptor: Authorization-Header automatisch setzen
axios.interceptors.request.use(
  (config) => {
    const user = getStoredUser();
    if (user && user.token && !config.headers['Authorization']) {
      config.headers['Authorization'] = `Bearer ${user.token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Response-Interceptor: bei 401 Token erneuern und Request wiederholen
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (!error.response || error.response.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    // Refresh-Token-Requests selbst nicht wiederholen
    if (originalRequest.url === API_ENDPOINTS.AUTH.REFRESH_TOKEN) {
      redirectToLogin();
      return Promise.reject(error);
    }

    if (isRefreshing) {
      // Andere gleichzeitige Requests in die Queue stellen
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((token) => {
          originalRequest.headers['Authorization'] = `Bearer ${token}`;
          return axios(originalRequest);
        })
        .catch((err) => Promise.reject(err));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    const user = getStoredUser();
    if (!user || !user.refreshToken) {
      isRefreshing = false;
      redirectToLogin();
      return Promise.reject(error);
    }

    try {
      const { data } = await axios.post(API_ENDPOINTS.AUTH.REFRESH_TOKEN, {
        refreshToken: user.refreshToken,
        remember: true,
      });

      const updatedUser = {
        ...user,
        token: data.accessToken,
        refreshToken: data.refreshToken,
      };
      localStorage.setItem('user', JSON.stringify(updatedUser));

      processQueue(null, data.accessToken);
      originalRequest.headers['Authorization'] = `Bearer ${data.accessToken}`;
      return axios(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      redirectToLogin();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);
